import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ValidationDashboardsTable.translations.json';
import {
  ColumnFiltersState,
  createColumnHelper,
  OnChangeFn,
} from '@tanstack/react-table';
import {
  DashboardType,
  DirectoryTableProps,
  DropdownItem,
  PopconfirmDelete,
  Table,
  useAlertContext,
  useMutateValidationDashboardQuery,
  ValidationDashboardListDto,
} from '@atlas-frontend/sdk';
import { memo, useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, Flex } from 'antd';
import * as Icon from 'react-feather';
import { UsedDataCell } from './UsedDataCell';
import { EditDashboardModal } from './EditDashboardModal';
import { useDashboardType } from './useDashboardType';

const { addTranslations, t } = i18nNsFactory('validation-dashboards-table');
addTranslations(translations);

const columnHelper = createColumnHelper<ValidationDashboardListDto>();

export type ValidationDashboardsTableProps = DirectoryTableProps<any> & {
  setFilters: OnChangeFn<ColumnFiltersState>;
  refetchData: () => void;
  onClickOpen: (externalCode: string) => void;
  onClickDuplicate: (
    externalCode: string,
    dashboardType?: DashboardType,
  ) => void;
  onClickChangeData: (
    externalCode: string,
    dashboardType?: DashboardType,
  ) => void;
  onClickSourceData?: (data: ValidationDashboardListDto) => void;
  onClickAlgorithmData?: (data: ValidationDashboardListDto) => void;
};

export function ValidationDashboardsTable(
  props: Readonly<ValidationDashboardsTableProps>,
) {
  const {
    refetchData,
    onClickOpen,
    onClickDuplicate,
    onClickChangeData,
    onClickSourceData,
    onClickAlgorithmData,
  } = props;
  const [selectedDashboard, setSelectedDashboard] =
    useState<ValidationDashboardListDto | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: t('column.name'),
        meta: {
          width: '40ch',
        },

        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('id', {
        header: t('column.usedData'),
        cell: (props) => <UsedDataCell data={props.row.original} />,
        meta: {
          width: '30ch',
        },
        enableSorting: false,
        enableColumnFilter: false,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: t('column.description'),
        meta: {
          width: '20ch',
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('createdBy.username', {
        id: 'createdBy',
        header: t('column.createdBy'),
        meta: {
          width: '12ch',
        },
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (props) => (
          <ActionsCell
            data={props.row.original}
            onEdit={() => setSelectedDashboard(props.row.original)}
            refetchTableData={refetchData}
            onClickOpen={onClickOpen}
            onClickDuplicate={onClickDuplicate}
            onClickChangeData={onClickChangeData}
            onClickSourceData={onClickSourceData}
            onClickAlgorithmData={onClickAlgorithmData}
          />
        ),
        meta: {
          width: '18ch',
        },
      }),
    ],
    [
      refetchData,
      onClickOpen,
      onClickDuplicate,
      onClickChangeData,
      onClickSourceData,
      onClickAlgorithmData,
    ],
  );

  return (
    <>
      <Table
        name="validation-dashboards-table"
        columns={columns}
        hideClearFilters
        manualFiltering
        manualSorting
        scrollContainerSelector={props.scrollContainerSelector as string}
        sticky
        virtual
        {...props}
      />
      {selectedDashboard && (
        <EditDashboardModal
          validationDashboard={selectedDashboard}
          onSuccess={() => {
            refetchData();
            setSelectedDashboard(null);
          }}
          onClose={() => setSelectedDashboard(null)}
        />
      )}
    </>
  );
}

export const ActionsCell = memo(
  ({
    data,
    onEdit,
    refetchTableData,
    onClickOpen,
    onClickDuplicate,
    onClickChangeData,
    onClickSourceData,
    onClickAlgorithmData,
  }: {
    data: ValidationDashboardListDto;
    onEdit: () => void;
    refetchTableData: () => void;
    onClickOpen: (externalCode: string) => void;
    onClickDuplicate: (
      externalCode: string,
      dashboardType?: DashboardType,
    ) => void;
    onClickChangeData: (
      externalCode: string,
      dashboardType?: DashboardType,
    ) => void;
    onClickSourceData?: (data: ValidationDashboardListDto) => void;
    onClickAlgorithmData?: (data: ValidationDashboardListDto) => void;
  }) => {
    const { mutate } = useMutateValidationDashboardQuery();
    const { resetAlert, setSuccess, setError } = useAlertContext();
    const dashboardType = useDashboardType(data.dashboardTypeId);

    const handleDelete = useCallback(() => {
      resetAlert();
      mutate(
        {
          id: data.id,
        },
        {
          onSuccess: () => {
            refetchTableData();
            setSuccess();
          },
          onError: (error) => {
            const apiError = (error.response?.data.errors ?? [])[0];
            if (apiError) {
              setError(t(`alert.error.code.${apiError.code}`));
            }
          },
        },
      );
    }, [mutate, setSuccess, refetchTableData, resetAlert, setError, data]);

    const actionMenu = useMemo(
      () => ({
        items: [
          {
            label: <DropdownItem>{t('dropdown.withUsedData')}</DropdownItem>,
            key: 'withUsedData',
            onClick: () => {
              onClickOpen(data.externalCode);
            },
          },
          {
            label: <DropdownItem>{t('dropdown.withNewData')}</DropdownItem>,
            key: 'withNewData',
            onClick: () => {
              onClickChangeData(data.externalCode, dashboardType);
            },
          },
          {
            label: <DropdownItem>{t('dropdown.withNewDataCopy')}</DropdownItem>,
            key: 'withNewDataCopy',
            onClick: () => {
              onClickDuplicate(data.externalCode, dashboardType);
            },
          },
        ],
      }),
      [
        onClickOpen,
        data.externalCode,
        dashboardType,
        onClickChangeData,
        onClickDuplicate,
      ],
    );

    const infoMenu = useMemo(
      () => ({
        items: [
          {
            label: <DropdownItem>{t('dropdown.openSourceData')}</DropdownItem>,
            key: 'openSourceData',
            onClick: () => onClickSourceData?.(data),
          },
          {
            label: <DropdownItem>{t('dropdown.openAlgorithms')}</DropdownItem>,
            key: 'openAlgorithms',
            onClick: () => onClickAlgorithmData?.(data),
          },
          {
            label: <DropdownItem>{t('dropdown.edit')}</DropdownItem>,
            key: 'edit',
            onClick: onEdit,
          },
          {
            label: (
              <PopconfirmDelete
                stopClickPropagation
                onDelete={handleDelete}
                genitiveModelName={t('delete.genitive')}
              />
            ),
            key: 'delete',
          },
        ],
      }),
      [onEdit, handleDelete, onClickSourceData, onClickAlgorithmData, data],
    );

    return (
      <Flex gap="small" justify="space-between">
        <Dropdown menu={actionMenu} trigger={['click']}>
          <Button id={'dashboard-actions'}>
            {t('dropdown.openDashboard')}
            <Icon.ChevronDown size={18} />
          </Button>
        </Dropdown>
        <Flex align="center">
          <Dropdown menu={infoMenu} trigger={['click']} placement="bottomRight">
            <Button
              id={'dashboard-actions-2'}
              type="text"
              shape="circle"
              icon={<Icon.MoreVertical size={20} />}
              size="small"
            />
          </Dropdown>
        </Flex>
      </Flex>
    );
  },
);
